import { useCallback, useEffect, useState } from 'react';
import type { AppSession, DevicesView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo } from '../../lib/format';
import { Icon } from '../../components/Mark';
import { Card, Empty } from '../../components/Bits';

/**
 * Connecting a cuff, a scale or a watch.
 *
 * What a patient is told here is the whole bargain: what we receive, what we never receive, and
 * that a device fills in numbers but never decides on its own that something is wrong — the
 * decide to do. Every claim on this page is true of the deployment they are looking at, which is
 * why the provider comes from the server rather than from a list of logos.
 */
export function DevicesPage({ session, onChanged }: { session: AppSession; onChanged: () => Promise<void> }) {
  const [view, setView] = useState<DevicesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setView(await api.devices(session.token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your devices.');
    }
  }, [session.token]);
  useEffect(() => {
    void load();
  }, [load]);

  const link = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.linkDevice(session.token);
      await load();
      await onChanged();
      // A real aggregator hands back its own page to finish the link in; the simulated one does not.
      if (r.url && !r.url.endsWith('/#/devices')) location.href = r.url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start that link.');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (id: string) => {
    if (!confirm('Disconnect this device? Nothing already recorded is removed.')) return;
    setBusy(true);
    try {
      await api.unlinkDevice(id, session.token);
      await load();
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not disconnect that device.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shell-narrow" style={{ margin: '0 auto' }}>
      <div className="page-head">
        <div>
          <h1>Connected devices</h1>
          <span className="hint">Let a cuff, a scale or a watch fill in some of this for you</span>
        </div>
        <a className="button secondary small" href="#/health">
          <Icon name="back" size={15} /> Back to health
        </a>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      {view && !view.provider ? (
        <Card>
          <Empty title="No device connections on this deployment">Your practice has not turned on device sync. The readings you log by hand are what count.</Empty>
        </Card>
      ) : null}

      {view?.provider ? (
        <Card>
          <h2>{view.provider.label}</h2>
          <p className="hint">{view.provider.supplies}.</p>
          {view.devices.length === 0 ? (
            <button type="button" className="primary" disabled={busy} onClick={() => void link()}>
              {busy ? 'Connecting…' : 'Connect a device'}
            </button>
          ) : (
            <ul className="devices">
              {view.devices.map((d) => (
                <li key={d.id}>
                  <span className="bubble bubble-blue bubble-sm">
                    <Icon name="watch" size={17} />
                  </span>
                  <span className="row-main">
                    <span className="row-title">{d.label}</span>
                    <span className="row-sub">{d.lastSampleAt ? `Last reading ${fmtAgo(d.lastSampleAt)}` : 'Connected, nothing received yet'}</span>
                  </span>
                  <button type="button" className="quiet small" disabled={busy} onClick={() => void unlink(d.id)}>
                    Disconnect
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      <Card>
        <h2>What this shares</h2>
        <ul className="plain">
          <li>What we receive: temperature, weight, resting pulse, steps and sleep.</li>
          <li>What we never receive: your location, your messages, or anything else on the device.</li>
          <li>Your care team sees the same numbers you do, which is the point of connecting it.</li>
          <li>You can disconnect at any time. Readings already taken stay in your record.</li>
        </ul>
        <p className="hint small">
          A device fills in numbers; it never raises an alarm on its own. What your team acts on is what you tell them — a temperature you took, a symptom you reported, a dose you ticked off.
        </p>
      </Card>
    </div>
  );
}
